import { WORKDAY_HIRE_API } from './api';
import type { WorkdayHireType } from './hireType';

/**
 * Where the chosen contingent worker's id is written.
 *
 * A conversion ends a contract and then hires, and the contract-end step needs
 * Workday's *worker* id — the applicant id alone will not do it. The select
 * can only hold one value, so the worker id is written to its own field as
 * the pick is made.
 */
export const CONTINGENT_WORKER_FIELD = 'contingent_worker_id';

interface ApplicantOption {
  id: string;
  label: string;
  /** Present on a conversion; what ends the contract. */
  workerId?: string;
}

/**
 * Turns a lookup field into a search box that asks the server per keystroke.
 *
 * Deliberately returns nothing for an empty query, which is what stops the
 * field fetching on mount: the applicant collection is ~4,200 rows and ~44MB,
 * and pulling it to identify one person is what timed out.
 *
 * Workday's applicant API accepts an id or an email and has no name filter of
 * any kind, so a typed name legitimately returns nothing. That is an answer,
 * not a failure — and several people can share an email, so several matches is
 * equally normal.
 */
export const makeApplicantLoader =
  (link: string, mode?: WorkdayHireType) =>
  async (inputValue?: string): Promise<ApplicantOption[]> => {
    const query = (inputValue || '').trim();
    if (!query) return [];

    const response = await WORKDAY_HIRE_API.applicantLookup(link, query, mode);
    return (response?.data?.results || []).map((applicant) => ({
      id: applicant.id,
      // Both, because the id is what a recruiter searched by and the name is
      // how they confirm it is the right person.
      label: applicant.name
        ? `${applicant.id} — ${applicant.name}`
        : applicant.id,
      ...(applicant.worker_id ? { workerId: applicant.worker_id } : {}),
    }));
  };

/**
 * Writes the chosen worker id alongside the applicant, and clears it when the
 * pick is cleared.
 *
 * Partial data is worse than none here: the backend rejects a contract-end set
 * that is missing a piece, precisely because ending the contract and then
 * failing the hire leaves someone neither contractor nor employee.
 */
export const makeApplicantOnChange =
  (setFieldValue: (_name: string, _value: unknown) => void) =>
  (selected?: ApplicantOption | null) =>
    setFieldValue(CONTINGENT_WORKER_FIELD, selected?.workerId ?? '');
